/**
 * @typedef {{label?: String, key: String, sortable?: Boolean}} Column
 */

/**
 * @param selected
 * @param allRows
 * @param rowOrRows
 * @param key
 * @returns {*[]}
 */
export function simpleSelect(selected, allRows, rowOrRows, key = null) {
  if (Array.isArray(rowOrRows)) {
    const allChecked = selected.length === allRows.length;
    const someChecked = selected.length > 0 && selected.length < allRows.length;
    if (allChecked) {
      selected = [];
    } else if (someChecked) {
      selected = [];
    } else {
      selected = [...allRows];
    }
  } else {
    let index = selected.findIndex((item) => {
      if (key) {
        return item[key] === rowOrRows[key];
      }
      return JSON.stringify(item) === JSON.stringify(rowOrRows);
    });
    if (index === -1) {
      selected.push(rowOrRows);
    } else {
      selected.splice(index, 1);
    }
  }
  return selected;
}

/**
 * @param {CreateElement} h
 * @returns {VNode}
 */
function renderTableStyle(h) {
  return h("style", { attrs: { scoped: true } }, [
    `
        .st-table-heading {
          display: flex;
          font-family: 'Cera Pro', sans-serif;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          gap: 0.5rem;
          padding: 0.5rem;
          margin: 0;
          border: none;
          background-color: transparent;
          cursor: default;
        }
        .st-table-heading-sortable {
          cursor: pointer;
        }
        .st-table-heading-sortable i {
        }
        .st-table-select {
          --size: 28px;
          --select-color: #fff;
          --select-background-color: #154a6a;
          --border-radius: 0.5rem;
          display: flex;
          position: relative;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          margin: 0;
          border: none;
          background-color: transparent;
          cursor: default;
          border: 2px solid var(--select-background-color);
          border-radius: var(--border-radius);
          width: var(--size);
          height: var(--size);
        }
        .st-table-select input[type="checkbox"] {
          opacity: 0;
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 3;
          cursor: pointer;
        }
        .st-table-select span {
          position: absolute;
          inset: 0;
          z-index: 1;
          background-color: transparent;
        }
        .st-table-select i {
          z-index: 2;
        }
        .st-table-select input[type="checkbox"]:checked + span {
          background-color: var(--select-background-color);
        }
        .st-table-select input[type="checkbox"]:checked + span + i {
          color: var(--select-color);
        }
        .st-table-empty-state {
          display: flex;
          width: 100%;
          height: 100%;
          text-align: center;
          align-items: center;
          justify-content: center;
          align-content: center;
          flex-wrap: wrap;
        }
      `,
  ]);
}

/**
 * @param {CreateElement} h
 * @param {String} type
 * @param {VNodeChildren} elements
 * @returns {VNode}
 */
function createTableCell(h, type, elements) {
  const cellClass = {
    th: this.tableHeadCellClass,
    td: this.tableBodyCellClass,
  }[type];
  const cellAttrs = {
    th: { scope: "col" },
    td: {},
  }[type];
  return h(
    type,
    {
      class: cellClass,
      attrs: cellAttrs,
    },
    elements
  );
}

/**
 * @param {CreateElement} h
 * @param {Column} column
 * @returns {VNode}
 */
function renderTableHeadingLabel(h, column) {
  return h("span", { class: "st-table-heading" }, [column.label]);
}

/**
 * @param {CreateElement} h
 * @param {Column} column
 * @returns {VNode}
 */
function renderTableHeadingLabelSortable(h, column) {
  return h(
    "button",
    {
      class: "st-table-heading st-table-heading-sortable",
      on: {
        click: () => {
          this.setSort(column.key);
        },
      },
    },
    [
      h("span", { class: "" }, [column.label]),
      h(
        "i",
        {
          class: {
            [this.sortIconAscending]:
              this.currentSort?.key === column.key &&
              this.currentSort?.order === "asc",
            [this.sortIconDescending]:
              this.currentSort?.key === column.key &&
              this.currentSort?.order === "desc",
          },
        },
        []
      ),
    ]
  );
}

/**
 * @param {Column} column
 * @param {CreateElement} h
 * @returns {VNode}
 */
function renderTableHeading(column, h) {
  const slotName = `${column.key}-header`;
  if (this.$scopedSlots[slotName]) {
    return createTableCell.call(this, h, "th", [
      this.$scopedSlots[slotName]({
        column,
      }),
    ]);
  } else {
    return createTableCell.call(this, h, "th", [
      column.sortable
        ? renderTableHeadingLabelSortable.call(this, h, column)
        : renderTableHeadingLabel.call(this, h, column),
    ]);
  }
}

/**
 * @param {CreateElement} h
 * @param {Object} row
 * @returns {VNode|null}
 */
function renderTableSelect(h, row = null) {
  if (null === this.value) {
    return null;
  }

  const key = this.selectKey || null;

  let checkboxIcon;
  let checkboxType = "checkbox";
  let isChecked;

  if (row === null) {
    const entriesExist = this.value.length > 0;
    const allSelected = this.rows.every(
      (row) =>
        this.value.findIndex((item) => {
          if (key) {
            return item[key] === row[key];
          }
          return JSON.stringify(item) === JSON.stringify(row);
        }) !== -1
    );
    const someSelected =
      !allSelected &&
      this.value.some(
        (item) =>
          this.rows.findIndex((row) => {
            if (key) {
              return item[key] === row[key];
            }
            return JSON.stringify(item) === JSON.stringify(row);
          }) !== -1
      );

    if (allSelected) {
      checkboxIcon = "fa-check";
    } else if (someSelected) {
      checkboxIcon = "fa-minus";
    }

    isChecked = entriesExist && (allSelected || someSelected);
  } else {
    isChecked =
      this.value.findIndex((item) => {
        if (key) {
          return item[key] === row[key];
        }
        return JSON.stringify(item) === JSON.stringify(row);
      }) !== -1;
    checkboxIcon = isChecked ? "fa-check" : "";
  }

  return h(
    "div",
    {
      class: "st-table-select",
    },
    [
      h("input", {
        attrs: {
          type: checkboxType,
        },
        on: {
          change: (event) => {
            if (row) {
              this.select(row);
            } else {
              this.select(this.rows);
            }
          },
        },
        domProps: {
          checked: isChecked,
        },
      }),
      h("span", {}, []),
      h("i", { class: `fa ${checkboxIcon}` }),
    ]
  );
}

/**
 * @param {CreateElement} h
 * @param {Column} column
 * @param {Object} row
 * @returns {VNode}
 */
function renderTableData(column, h, row) {
  const slotName = `${column.key}-data`;
  if (this.$scopedSlots[slotName]) {
    return createTableCell.call(this, h, "td", [
      this.$scopedSlots[slotName]({ row }),
    ]);
  } else {
    return createTableCell.call(this, h, "td", [row[column.key]]);
  }
}

/**
 * @param {CreateElement} h
 * @returns {VNode}
 */
function renderTable(h) {
  let tableSelectHead = renderTableSelect.call(this, h);
  if (tableSelectHead) {
    tableSelectHead = createTableCell.call(this, h, "th", [tableSelectHead]);
  }
  const getEmptyState = () => {
    if (this.getRowsSorted.length === 0) {
      return [
        h("tbody", {}, [
          h("tr", {}, [h("td", {
            attrs: {
              colspan: '100%'
            }
          }, [renderEmptyState.call(this, h)])]),
        ]),
      ];
    }
    return [];
  };
  const getRows = () => {
    if (this.getRowsSorted.length === 0) {
      return [];
    }
    return [
      h(
        "tbody",
        {
          class: this.tableBodyClass,
        },
        [
          ...this.getRowsSorted.map((row) => {
            let tableSelectData = renderTableSelect.call(this, h, row);
            if (tableSelectData) {
              tableSelectData = createTableCell.call(this, h, "td", [
                tableSelectData,
              ]);
            }
            return h(
              "tr",
              {
                class: this.tableBodyRowClass,
              },
              [
                tableSelectData,
                ...this.columns.map((column) => {
                  return renderTableData.call(this, column, h, row);
                }),
              ]
            );
          }),
        ]
      ),
    ];
  };
  return h(
    "table",
    {
      class: this.tableClass,
    },
    [
      h(
        "thead",
        {
          class: this.tableHeadClass,
        },
        [
          h(
            "tr",
            {
              class: this.tableHeadRowClass,
            },
            [
              tableSelectHead,
              ...this.columns.map((column) => {
                return renderTableHeading.call(this, column, h);
              }),
            ]
          ),
        ]
      ),
      ...getEmptyState(),
      ...getRows(),
    ]
  );
}

/**
 * @param {CreateElement} h
 * @returns {VNode}
 */
function renderEmptyState(h) {
  if (this.$scopedSlots["empty-state"]) {
    return h(
      "div",
      {
        class: "st-table-empty-state",
      },
      [this.$scopedSlots["empty-state"]()]
    );
  }
  return h(
    "div",
    {
      class: "st-table-empty-state",
    },
    [h("p", {}, ["No data"])]
  );
}

export default {
  name: "SimpleTable",
  props: {
    /**
     * @type {Column[]}
     */
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    tableClass: {
      type: String,
      required: false,
      default: "table table-striped table-bordered table-hover",
    },
    tableHeadClass: {
      type: String,
      required: false,
      default: "",
    },
    tableBodyClass: {
      type: String,
      required: false,
      default: "",
    },
    tableHeadRowClass: {
      type: String,
      required: false,
      default: "",
    },
    tableHeadCellClass: {
      type: String,
      required: false,
      default: "",
    },
    tableBodyRowClass: {
      type: String,
      required: false,
      default: "",
    },
    tableBodyCellClass: {
      type: String,
      required: false,
      default: "",
    },
    sort: {
      type: Object,
      required: false,
      default: null,
    },
    selectKey: {
      type: String,
      required: false,
      default: null,
    },
    value: {
      type: Array,
      required: false,
      default: () => null,
    },
    sortCaseAscValue: {
      type: String,
      required: false,
      default: "asc",
    },
    sortCaseDescValue: {
      type: String,
      required: false,
      default: "desc",
    },
    sortFunction: {
      type: Function,
      required: false,
      default: function (a, b, key, orderModifier) {
        let aKey = a[key];
        let bKey = b[key];
        if (!Number.isNaN(Number(aKey)) && !Number.isNaN(Number(bKey))) {
          aKey = Number(aKey);
          bKey = Number(bKey);
        }
        if (aKey < bKey) {
          return -1 * orderModifier;
        }
        if (aKey > bKey) {
          return orderModifier;
        }
        return 0;
      },
    },
    sortIconAscending: {
      type: String,
      required: false,
      default: "fa-solid fa-sort-asc",
    },
    sortIconDescending: {
      type: String,
      required: false,
      default: "fa-solid fa-sort-desc",
    },
  },
  data() {
    return {
      currentSort: this.sort || null,
    };
  },
  computed: {
    getRowsSorted() {
      if (!this.currentSort) {
        return this.rows;
      }
      const { key, order } = this.currentSort;
      let orderModifier = 1;
      switch (order) {
        case this.sortCaseAscValue:
          orderModifier = 1;
          break;
        case this.sortCaseDescValue:
          orderModifier = -1;
          break;
        default:
          console.warn("[SimpleTable] Unknown order type");
      }
      if (key && this.sortFunction) {
        return this.rows.sort((a, b) => {
          return this.sortFunction(a, b, key, orderModifier);
        });
      }
      return this.rows;
    },
  },
  methods: {
    setSort(key) {
      const updateSort = () => {
        if (!this.currentSort) {
          this.currentSort = {
            key,
            order: this.sortCaseAscValue,
          };
          return;
        }
        if (
          this.currentSort.key === key &&
          this.currentSort.order === this.sortCaseAscValue
        ) {
          this.currentSort.order = this.sortCaseDescValue;
          return;
        }
        if (
          this.currentSort.key === key &&
          this.currentSort.order === this.sortCaseDescValue
        ) {
          this.currentSort = null;
          return;
        }
        this.currentSort = {
          key,
          order: this.sortCaseAscValue,
        };
      };
      updateSort();
      this.$emit("update:sort", this.currentSort);
    },
    select(rowOrRows) {
      this.$emit("select", rowOrRows);
    },
  },
  render(h) {
    return h(
      "div",
      {
        class: "simple-table table-responsive",
      },
      [renderTableStyle.call(this, h), renderTable.call(this, h)]
    );
  },
};
